/**
 * Size filter/search inputs to their placeholder text width (never wider).
 * Typed value may scroll inside; width stays locked to the placeholder measure.
 */

const ATTR = 'data-fit-placeholder'
const EXTRA_PX = 24 // padding/border + webkit search cancel + measuring slack

let canvas: HTMLCanvasElement | null = null
let observer: MutationObserver | null = null
let scheduled = false

function measureTextWidth(text: string, el: HTMLInputElement): number {
  const style = getComputedStyle(el)
  if (!canvas) canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')
  if (!ctx) return text.length * 8
  ctx.font = `${style.fontWeight} ${style.fontSize} ${style.fontFamily}`
  const textW = ctx.measureText(text || ' ').width
  const padL = Number.parseFloat(style.paddingLeft) || 0
  const padR = Number.parseFloat(style.paddingRight) || 0
  const borderL = Number.parseFloat(style.borderLeftWidth) || 0
  const borderR = Number.parseFloat(style.borderRightWidth) || 0
  return Math.ceil(textW + padL + padR + borderL + borderR + EXTRA_PX)
}

function isTarget(el: Element): el is HTMLInputElement {
  if (!(el instanceof HTMLInputElement)) return false
  if (el.dataset.fitPlaceholder === 'off') return false
  if (el.classList.contains('pw-select--search')) return true
  if (el.type === 'search' && el.closest('.workbench-panel-filters, .pw-panel__filters, .pw-filter-bar')) {
    return true
  }
  return false
}

export function fitPlaceholderInput(el: HTMLInputElement): void {
  const placeholder = el.getAttribute('placeholder') ?? ''
  if (placeholder === '') return
  const width = measureTextWidth(placeholder, el)
  el.style.setProperty('width', `${width}px`, 'important')
  el.style.setProperty('min-width', '0', 'important')
  el.style.setProperty('max-width', '100%', 'important')
  el.style.setProperty('flex', '0 1 auto', 'important')
  el.setAttribute(ATTR, '1')

  const field = el.closest('.pw-filter-field')
  if (field instanceof HTMLElement) {
    field.style.setProperty('flex', '0 1 auto', 'important')
    field.style.setProperty('width', 'auto', 'important')
    field.style.setProperty('min-width', '0', 'important')
    field.style.setProperty('max-width', '100%', 'important')
  }
}

function scan(root: ParentNode = document): void {
  root.querySelectorAll<HTMLInputElement>('input.pw-select--search, input[type="search"]').forEach((el) => {
    if (isTarget(el)) fitPlaceholderInput(el)
  })
}

function scheduleScan(): void {
  if (scheduled) return
  scheduled = true
  requestAnimationFrame(() => {
    scheduled = false
    scan()
  })
}

/** Start watching the document for filter/search inputs (idempotent). */
export function startFitPlaceholderInputs(): () => void {
  scan()
  if (observer) return () => stopFitPlaceholderInputs()

  observer = new MutationObserver((mutations) => {
    let needs = false
    for (const m of mutations) {
      if (m.type === 'attributes') {
        const t = m.target
        if (t instanceof HTMLInputElement && isTarget(t)) {
          fitPlaceholderInput(t)
        }
        continue
      }
      if (m.addedNodes.length > 0) needs = true
    }
    if (needs) scheduleScan()
  })

  observer.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['placeholder', 'class', 'type'],
  })

  window.addEventListener('resize', scheduleScan)

  return () => stopFitPlaceholderInputs()
}

export function stopFitPlaceholderInputs(): void {
  observer?.disconnect()
  observer = null
  window.removeEventListener('resize', scheduleScan)
}
